import React, { useCallback, useState, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { openInlineCommentInEditor } from '@confluence/comments-util';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks';
import {
	useInlineCommentsContext,
	useInlineCommentsDispatchContext,
} from '@confluence/comment-context';
import { useInlineCommentQueryParams } from '@confluence/comment';
import {
	GeneralShortcutListener,
	NEXT_COMMENT_SHORTCUT_IN_EDIT,
	PREV_COMMENT_SHORTCUT_IN_EDIT,
	NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
} from '@confluence/shortcuts';

const EditorInlineCommentShortcutListenerComponent = ({
	contentId,
	viewMode,
}: {
	contentId?: string;
	viewMode?: boolean;
}) => {
	const { navigateToComment } = useInlineCommentsDispatchContext();
	const { unresolvedInlineComments } = useInlineCommentsState();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activeHighlight } = useInlineCommentsContext();
	const [isNextCommentLoading, setIsNextCommentLoading] = useState(false);

	useEffect(() => {
		setIsNextCommentLoading(false);
	}, [activeHighlight]);

	const onNavigationClick = useCallback((nextMarkerRef?: string) => {
		if (!nextMarkerRef) return;
		const emitter = getEditorAnnotationEventEmitter();
		emitter.emit('setselectedannotation', nextMarkerRef);
		const commentElement = document.getElementById(nextMarkerRef);
		if (commentElement) {
			openInlineCommentInEditor(commentElement, nextMarkerRef);
		}
	}, []);

	const getCommentIndex = useCallback(
		(currentMarkerRef: string): number => {
			return unresolvedInlineComments?.findIndex((markerRef) => markerRef === currentMarkerRef);
		},
		[unresolvedInlineComments],
	);

	const handleInlineCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			const firstInlineCommentMarkerRef = document
				.querySelector('.ak-editor-annotation-blur')
				?.closest('[annotationtype="inlineComment"]')?.id;

			if (activeHighlight) {
				//Check if the next inline comment is still in process of being rendered. Otherwise, quickly pressing keys will force comment to open and close instead of navigating
				if (isNextCommentLoading) return;

				setIsNextCommentLoading(true);
				navigateToComment({
					action,
					triggeredByKeyboardShortcut: true,
					currentCommentIndex: getCommentIndex(activeHighlight),
					onNavigationClick,
					isEditor: true,
					removeCommentQueryParams,
					unresolvedInlineComments,
					createAnalyticsEvent,
					contentId,
				});
			} else {
				onNavigationClick(firstInlineCommentMarkerRef);
			}
		},
		[
			activeHighlight,
			onNavigationClick,
			removeCommentQueryParams,
			unresolvedInlineComments,
			createAnalyticsEvent,
			contentId,
			getCommentIndex,
			navigateToComment,
			isNextCommentLoading,
		],
	);

	return (
		<div data-testId="comments-shortcut-listener">
			<GeneralShortcutListener
				accelerator={viewMode ? PREV_COMMENT_SHORTCUT : PREV_COMMENT_SHORTCUT_IN_EDIT}
				listener={() => handleInlineCommentNavigation('previous')}
			/>
			<GeneralShortcutListener
				accelerator={viewMode ? NEXT_COMMENT_SHORTCUT : NEXT_COMMENT_SHORTCUT_IN_EDIT}
				listener={() => handleInlineCommentNavigation('next')}
			/>
		</div>
	);
};

export const EditorInlineCommentShortcutListener = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(EditorInlineCommentShortcutListenerComponent);
